import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { ArrowRight, Wallet, Layers, MessageSquare, ShieldCheck, Check, X } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { trackCtaClick } from "@/lib/analytics";

type Row = { feature: string; sub: string; thiia: string };
type Faq = { q: string; a: string };

export default function NoSubscriptionCrmPage() {
  const { t } = useTranslation();
  const rows = t("noSubCrm.rows", { returnObjects: true }) as Row[];
  const faq = t("noSubCrm.faq", { returnObjects: true }) as Faq[];
  const why = [
    { icon: Wallet, n: 1 },
    { icon: Layers, n: 2 },
    { icon: MessageSquare, n: 3 },
    { icon: ShieldCheck, n: 4 },
  ];

  const SignupCta = ({ where }: { where: string }) => (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button asChild size="lg" className="h-12 px-6">
        <Link to="/auth?mode=signup" onClick={() => trackCtaClick?.(`no-sub-crm-${where}`, "/auth?mode=signup")}>
          {t("noSubCrm.ctaPrimary")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="h-12 px-6">
        <Link to="/pricing">{t("noSubCrm.ctaSecondary")}</Link>
      </Button>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background text-foreground overflow-x-hidden">
      <PublicHeader />

      <section className="pt-32 pb-16 md:pt-44 md:pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm font-sans">
            {t("noSubCrm.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold leading-tight tracking-tight">
            {t("noSubCrm.h1")} <span className="text-primary">{t("noSubCrm.h1Accent")}</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            {t("noSubCrm.subtitle")}
          </p>
          <div className="mt-8">
            <SignupCta where="hero" />
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">{t("noSubCrm.whyTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {why.map(({ icon: Icon, n }) => (
              <Card key={n}>
                <CardContent className="p-6">
                  <Icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-display font-semibold text-lg mb-2">{t(`noSubCrm.why${n}Title`)}</h3>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">{t(`noSubCrm.why${n}Body`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">{t("noSubCrm.compareTitle")}</h2>
          <div className="w-full overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm font-sans">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left p-3">{t("noSubCrm.compareFeature")}</th>
                  <th className="text-left p-3">{t("noSubCrm.compareSub")}</th>
                  <th className="text-left p-3 text-primary">{t("noSubCrm.compareThiia")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.feature} className="border-t border-border">
                    <td className="p-3 font-medium">{r.feature}</td>
                    <td className="p-3 text-muted-foreground">
                      <span className="inline-flex items-start gap-2"><X className="h-4 w-4 mt-0.5 shrink-0" />{r.sub}</span>
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />{r.thiia}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">{t("noSubCrm.stepsTitle")}</h2>
          <ol className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <li key={n}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-display font-bold mb-3">{n}</span>
                    <h3 className="font-display font-semibold text-lg mb-2">{t(`noSubCrm.step${n}Title`)}</h3>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">{t(`noSubCrm.step${n}Body`)}</p>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ol>
          <div className="mt-8">
            <SignupCta where="steps" />
          </div>
        </div>
      </section>


      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">{t("noSubCrm.faqTitle")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`nosub-faq-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold min-h-[56px]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground font-sans leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">{t("noSubCrm.finalTitle")}</h2>
          <p className="text-muted-foreground font-sans mb-8">{t("noSubCrm.finalBody")}</p>
          <SignupCta where="final" />
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
